import dayjs from 'dayjs';
import { createSelector } from 'reselect';

import {
  BasketAnalyticsGreeksTableRowData,
  BasketAnalyticsPNLTableRowData,
} from 'components/basketAnalytics/BasketAnalyticsTabSection/BasketAnalyticsTabSection.types';
import {
  basketAnalyzeFormatGreekValue,
  basketAnalyzeGetCheckDateUTC,
} from 'components/basketOrders/BasketAnalyze/BasketAnalyze.helper';
import { analyzeGetDailyTargetTimestamps } from 'components/common/Analyse/analyse.helper';
import { GREEK_PRECISION, ORDER_SIDE, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { ORDER_TYPE } from 'constants/enums';
import { TIME_FORMATS } from 'constants/timeformats';
import { round_by_contract_size as roundByContractSize } from 'helpers/assetUtils';
import { dateFormat, toUTC } from 'helpers/day';
import {
  divide,
  filter,
  flip,
  groupBy,
  map,
  mapObjIndexed,
  max,
  min,
  multiply,
  partition,
  pipe,
  pluck,
  reduce,
  sort,
  subtract,
  sum,
  uniq,
} from 'helpers/ramda';
import {
  cropAfterDecimals,
  isEmpty,
  isFutures,
  numberCommaSeparator,
  numberPrettified,
} from 'helpers/utils';
import { allOpenPositionsSelector } from 'selectors/holdingsSelectors';
import { TickerObjectItem } from 'stores/ticker/ticker.types';
import { NumberSign, PNLData } from 'types/basketAnalyze';
import { ContractType } from 'types/IProducts';
import type IState from 'types/Istore';
import { ProductById } from 'types/ITrade';
import { PositionAnalyticsState } from 'types/positionAnalyse';

import { productsSelector } from './tradeSelectors';

const isInfinity = value => value === Infinity || value === -Infinity;

const formatValue = pipe(Number, num => cropAfterDecimals(num, 2), numberPrettified);

const getNumberSign = number => {
  if (number === 0 || !Number.isFinite(number)) return NumberSign.DEFAULT;
  return number > 0 ? NumberSign.POSITIVE : NumberSign.NEGATIVE;
};

// ========== Pure Functions (Zustand-compatible) ==========

/**
 * Pure function to get PnL data from position analyse state
 */
const getPositionAnalysePnlData = (positionAnalyse: PositionAnalyticsState) => {
  const {
    max_loss: maxLoss,
    max_profit: maxProfit,
    break_even_point: breakEvenPoints,
  } = positionAnalyse?.positionAnalytics?.data ?? {};

  const maxProfitSign = getNumberSign(Number(maxProfit));
  const maxLossSign = getNumberSign(Number(maxLoss));

  const formattedBreakEvenPoints =
    Array.isArray(breakEvenPoints) && !isEmpty(breakEvenPoints)
      ? pipe(
          map(point => cropAfterDecimals(Number(point), 2)),
          uniq
        )(breakEvenPoints)
      : ['NA'];

  const isInfinityProfit = isInfinity(Number(maxProfit));
  const isInfinityLoss = isInfinity(Number(maxLoss));

  const formattedMaxProfit = isInfinityProfit ? 'Unlimited' : formatValue(maxProfit);
  const formattedMaxLoss = isInfinityLoss ? 'Unlimited' : formatValue(maxLoss);

  const rewardByRiskRawValue = Math.abs(Number(maxProfit)) / Math.abs(Number(maxLoss));
  const isRewardByRiskRawValueInfinity = isInfinity(rewardByRiskRawValue);

  const rewardByRisk =
    isInfinityProfit || isInfinityLoss || isRewardByRiskRawValueInfinity
      ? 'NA'
      : numberPrettified(rewardByRiskRawValue);

  return {
    formattedMaxProfit,
    formattedMaxLoss,
    formattedBreakEvenPoints,
    rewardByRisk,
    maxProfitSign,
    isProfitInfinite: isInfinityProfit,
    maxLossSign,
    isLossInfinite: isInfinityLoss,
  };
};

/**
 * Pure function to get active orders for a specific asset
 */
const getPositionAnalyseActiveOrders = (
  orders: ReturnType<typeof positionAnalyseOrdersSelector>,
  activeAsset: string
) => {
  const ordersOfCurrentActiveAsset = Object.values(orders).filter(
    order => order?.asset === activeAsset && order.side
  );
  return ordersOfCurrentActiveAsset;
};

/**
 * Pure function to get target dates
 */
const getPositionAnalyseTargetDates = (
  ordersOfCurrentActiveAsset: ReturnType<typeof getPositionAnalyseActiveOrders>,
  products: ProductById,
  positionAnalyse: PositionAnalyticsState
) => {
  const { selectedRows = [], selectedTargetDate } = positionAnalyse?.pnlTab ?? {};
  if (!selectedRows.length && !selectedTargetDate) return [];

  const dailyTimestamps = analyzeGetDailyTargetTimestamps({
    selectedProductIds: selectedRows,
    orders: ordersOfCurrentActiveAsset,
    products,
  });
  return dailyTimestamps;
};

/**
 * Pure function to get target dates index map
 */
const getPositionAnalyseTargetDatesIndexMap = (targetDates: number[]) => {
  return targetDates.reduce((indexMap, time, index) => {
    // eslint-disable-next-line no-param-reassign
    indexMap[index] = time;
    return indexMap;
  }, {});
};

/**
 * Pure function to compute greeks for a single order
 * This replaces the selector-based approach with direct value access
 */
const computeGreekWithValue = ({
  size,
  greekValue,
  precision,
  side,
}: {
  size: number;
  greekValue: string | undefined;
  precision: number;
  side: string;
}) => {
  const rawGreekValue = Number(greekValue ?? 0);
  const sideMultiplier = side === ORDER_SIDE.BUY ? 1 : -1;
  const computedValue = size * rawGreekValue * sideMultiplier;

  return basketAnalyzeFormatGreekValue({
    greekValue: computedValue,
    precision,
  });
};

let previousResultPnlsMap: Record<string, BasketAnalyticsPNLTableRowData> = {};

/**
 * Pure function to get Greeks and PnL data
 * Now accepts Zustand maps as parameters instead of using selectors
 */
const getPositionAnalyseGreeksAndPnLData = (
  ordersOfCurrentActiveAsset: ReturnType<typeof getPositionAnalyseActiveOrders>,
  products: ProductById,
  positionAnalyse: PositionAnalyticsState,
  markPriceMap: Map<number, number>,
  deltaMap: Map<number, string | undefined>,
  gammaMap: Map<number, string | undefined>,
  thetaMap: Map<number, string | undefined>,
  vegaMap: Map<number, string | undefined>
): [BasketAnalyticsGreeksTableRowData[], BasketAnalyticsPNLTableRowData[]] => {
  const { pnlTab, positionAnalytics, positions } = positionAnalyse ?? {};
  const { result_pnls: resultPnls = [] } = positionAnalytics?.data ?? {};
  const { lastResolvedTargetPrice } = pnlTab;

  // Build a new cache from latestResultPnls
  const updatedMap = { ...previousResultPnlsMap };

  resultPnls.forEach(pnl => {
    const spotPrice = Math.floor(Number(pnl.spot_price));

    Object.entries(pnl.individual_pnls ?? {}).forEach(([productId, value]) => {
      updatedMap[`${spotPrice}-${productId}`] = value;
    });
  });

  // Replace the old cache with updated one
  previousResultPnlsMap = updatedMap;

  return ordersOfCurrentActiveAsset.reduce<
    [BasketAnalyticsGreeksTableRowData[], BasketAnalyticsPNLTableRowData[]]
  >(
    ([greeksAcc, pnlAcc], order) => {
      const { product_id: orderProductId, size, side, symbol, asset: orderAsset } = order;
      const selectedProduct = products?.[orderProductId];
      const rawSize = Number(size ?? 0) * Number(selectedProduct?.contract_value ?? 0);
      const isContractTypeFutures = isFutures(order.contract_type);
      const isMarketOrderType = order.order_type === ORDER_TYPE.MARKET;

      const markPriceValue = markPriceMap.get(order.product_id);
      const entryPrice = isMarketOrderType
        ? Number(
            cropAfterDecimals(
              markPriceValue,
              selectedProduct?.settling_asset?.minimum_precision
            )
          )
        : Number(order.limit_price);

      const key = `${Math.floor(lastResolvedTargetPrice)}-${orderProductId}`;
      const cachedPnl = previousResultPnlsMap[key];

      const estPrice = Number(cachedPnl?.exit_price ?? 0);

      let targetPnl;
      // for short/sell
      if (side === ORDER_SIDE.SELL) {
        targetPnl =
          -1 *
          Number(size ?? 0) *
          Number(selectedProduct?.contract_value) *
          (Number(estPrice) - entryPrice);
      }
      // for long/buy
      if (side === ORDER_SIDE.BUY) {
        targetPnl =
          1 *
          Number(size ?? 0) *
          Number(selectedProduct?.contract_value) *
          (Number(estPrice) - entryPrice);
      }

      let delta;
      if (isContractTypeFutures) {
        const adjustedSignBasedDelta =
          order.side === ORDER_SIDE.BUY
            ? Number(Number(rawSize) * 1.0)
            : Number(Number(rawSize) * -1.0);
        delta = basketAnalyzeFormatGreekValue({
          greekValue: adjustedSignBasedDelta,
          precision: GREEK_PRECISION.DELTA,
        });
      } else {
        delta = computeGreekWithValue({
          size: Number(rawSize ?? 0),
          greekValue: deltaMap.get(orderProductId),
          precision: GREEK_PRECISION.DELTA,
          side,
        });
      }

      const gamma = computeGreekWithValue({
        size: Number(rawSize ?? 0),
        greekValue: gammaMap.get(orderProductId),
        precision: GREEK_PRECISION.GAMMA,
        side,
      });

      const theta = computeGreekWithValue({
        size: Number(rawSize ?? 0),
        greekValue: thetaMap.get(orderProductId),
        precision: GREEK_PRECISION.THETA,
        side,
      });

      const vega = computeGreekWithValue({
        size: Number(rawSize ?? 0),
        greekValue: vegaMap.get(orderProductId),
        precision: GREEK_PRECISION.VEGA,
        side,
      });

      const roundedSize = Number(
        roundByContractSize(rawSize, selectedProduct?.contract_value)
      );

      const greeksTableRowData = {
        product_id: orderProductId,
        side,
        symbol,
        delta,
        gamma,
        theta,
        vega,
        size: roundedSize,
        asset: orderAsset,
      };

      const position = positions?.find(
        positionObject => positionObject.product_id === orderProductId
      );

      const pnlTableRowData = {
        product_id: orderProductId,
        side,
        symbol,
        size: roundedSize,
        asset: orderAsset,
        est_price: Number(cropAfterDecimals(estPrice, 2)),
        entry_price: Number(cropAfterDecimals(entryPrice, 2)),
        target_pnl: Number(cropAfterDecimals(targetPnl, 2)),
        position,
      };

      greeksAcc.push(greeksTableRowData);
      pnlAcc.push(pnlTableRowData);

      return [greeksAcc, pnlAcc];
    },
    [[], []]
  );
};

/**
 * Pure function to get result spot prices
 */
const getPositionAnalyseResultSpotPrices = (positionAnalyse: PositionAnalyticsState) => {
  const { positionAnalytics } = positionAnalyse ?? {};
  const { result_pnls: resultPnls = [] } = positionAnalytics?.data ?? {};
  const resultSpotPrices: number[] =
    resultPnls?.map(resultPnl => Math.trunc(Number(resultPnl.spot_price))) ?? [];
  return resultSpotPrices;
};

/**
 * Pure function to get expiry spot prices
 */
const getPositionAnalyseExpirySpotPrices = (positionAnalyse: PositionAnalyticsState) => {
  return pipe(
    map((expiryPnl: PNLData) => parseInt(expiryPnl.spot_price, 10).toString()),
    uniq,
    sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
  )(positionAnalyse?.positionAnalytics?.data?.expiry_pnls || []);
};

/**
 * Pure function to get product ticker pair list
 * Now accepts Zustand data as parameters
 */
const getPositionAnalyseProductTickerPairList = (
  allOptionsTicker: TickerObjectItem[],
  products: ProductById,
  activeAsset: string
) => {
  return allOptionsTicker
    .filter(ticker => Number.isSafeInteger(ticker?.product_id))
    .map(ticker => {
      return { ticker, product: products?.[ticker?.product_id!] };
    })
    .filter(contractData => {
      const { product } = contractData;
      const isNotFutures = !isEmpty(product) && !isFutures(product.contract_type);

      const settlementDate = isNotFutures
        ? dayjs(toUTC(product.settlement_time as Date))
        : null;
      const checkDateUTC = basketAnalyzeGetCheckDateUTC();

      return (
        product?.underlying_asset?.symbol === activeAsset &&
        (!isNotFutures || settlementDate?.isSame(checkDateUTC, 'day'))
      );
    });
};

/**
 * Pure function to get result spot price labels max
 */
const getPositionAnalyseResultSpotPriceLabelsMax = (
  positionAnalyseResultSpotPrices: number[]
) => {
  return pipe(
    map(Number),
    reduce<number, number>(max, -Infinity)
  )(positionAnalyseResultSpotPrices);
};

/**
 * Pure function to get result spot price labels min
 */
const getPositionAnalyseResultSpotPriceLabelsMin = (
  positionAnalyseResultSpotPrices: number[]
) => {
  return pipe(
    map(Number),
    reduce<number, number>(min, Infinity)
  )(positionAnalyseResultSpotPrices);
};

/**
 * Pure function to get open interest categories
 */
const getPositionAnalyseOpenInterestCategories = (
  productTickerPairList: ReturnType<typeof getPositionAnalyseProductTickerPairList>
) => {
  return sort(
    (a, b) => a - b,
    uniq(
      pluck(
        'strike_price',
        map(productTickerPair => productTickerPair.product, productTickerPairList)
      )
    )
  );
};

/**
 * Pure function to get grouped OI by strike prices coordinates
 * Now accepts Zustand OI map as parameter
 */
const getPositionAnalyseGroupOiByStrikePricesCoordinates = (
  productTickerPairList: ReturnType<typeof getPositionAnalyseProductTickerPairList>,
  oiMap: Map<number, string>
) => {
  const [callOptionsContracts, putOptionsContracts] = partition(
    ({ product }) => product.contract_type === ContractType.CallOptions,
    productTickerPairList
  );

  const getOiByStrikePrice = pipe<
    [typeof productTickerPairList],
    { [strike: string]: typeof productTickerPairList },
    { [strikePrice: string]: number }
  >(
    groupBy(({ product }) => product.strike_price ?? '0'),
    mapObjIndexed(
      pipe(
        map(({ ticker }) => {
          const productId = ticker.product_id as number;
          return Number(oiMap.get(productId) ?? 0);
        }),
        sum
      )
    )
  );

  const callOiByStrikePrices = getOiByStrikePrice(callOptionsContracts);
  const putOiByStrikePrices = getOiByStrikePrice(putOptionsContracts);

  const callsStrikePrices =
    Object.keys(callOiByStrikePrices)?.map(callOiByStrikePrice => {
      return {
        x: callOiByStrikePrice,
        y: callOiByStrikePrices[callOiByStrikePrice],
      };
    }) ?? [];

  const putsStrikePrices =
    Object.keys(putOiByStrikePrices)?.map(putOiByStrikePrice => {
      return {
        x: putOiByStrikePrice,
        y: putOiByStrikePrices[putOiByStrikePrice],
      };
    }) ?? [];

  return [callsStrikePrices, putsStrikePrices];
};

/**
 * Pure function to get open interest max
 * Now accepts Zustand OI map as parameter
 */
const getPositionAnalyseOpenInterestMax = (
  productTickerPairList: ReturnType<typeof getPositionAnalyseProductTickerPairList>,
  oiMap: Map<number, string>
) => {
  return pipe(
    map(productTickerPair => {
      const selectedProductOIValue = oiMap.get(productTickerPair.product.id);
      return Number(selectedProductOIValue ?? 0);
    }),
    map(Math.abs),
    reduce<number, number>(max, -Infinity)
  )(productTickerPairList);
};

/**
 * Pure function to get expiry PnL coordinates
 */
const getPositionAnalyseExpiryPnlCoordinates = (
  expirySpotPrices: string[],
  positionAnalyse: PositionAnalyticsState
) => {
  return expirySpotPrices.map(expirySpotPrice => {
    const selectedPnl =
      positionAnalyse?.positionAnalytics?.data?.expiry_pnls?.find(
        expiryPnl => parseInt(expiryPnl.spot_price, 10) === Number(expirySpotPrice)
      )?.pnl ?? 0;
    return {
      x: Number(expirySpotPrice),
      y: Number(selectedPnl),
    };
  });
};

/**
 * Pure function to get target date PnL coordinates
 */
const getPositionAnalyseTargetDatePnlCoordinates = (
  expirySpotPrices: string[],
  positionAnalyse: PositionAnalyticsState,
  defaultTargetPrice: number
) => {
  return expirySpotPrices.map(expirySpotPrice => {
    const selectedTargetPriceOfResulPnl =
      positionAnalyse?.positionAnalytics?.data?.result_pnls?.find(resultPnl => {
        return (
          Math.floor(parseInt(resultPnl?.spot_price, 10)) ===
          Math.floor(positionAnalyse?.pnlTab?.lastResolvedTargetPrice)
        );
      });
    const selectedResultPnl = positionAnalyse?.positionAnalytics?.data?.result_pnls?.find(
      resultPnl => parseInt(resultPnl?.spot_price, 10) === Number(expirySpotPrice)
    );
    const selectedExpiryPnl = positionAnalyse?.positionAnalytics?.data?.expiry_pnls?.find(
      expiryPnl => parseInt(expiryPnl?.spot_price, 10) === Number(expirySpotPrice)
    );
    return {
      x: Number(expirySpotPrice),
      y: Number(selectedResultPnl?.pnl),
      resultPnl: selectedResultPnl?.pnl,
      expiryPnl: selectedExpiryPnl?.pnl,
      resultPnlFormatted: numberCommaSeparator(
        Math.trunc(Number(selectedResultPnl?.pnl) * 100) / 100
      ),
      expiryPnlFormatted: numberCommaSeparator(
        Math.trunc(Number(selectedExpiryPnl?.pnl) * 100) / 100
      ),
      selectedExpiryDate: dateFormat(
        positionAnalyse?.pnlTab?.selectedTargetDate,
        TIME_FORMATS.ddd_DD_MMM
      ),
      settlingAsset: VANILLA_SETTLING_ASSET,
      profit: Number(selectedTargetPriceOfResulPnl?.pnl),
      formattedProfit: numberCommaSeparator(
        Number(selectedTargetPriceOfResulPnl?.pnl ?? 0).toFixed(2)
      ),
      profitPercentage: Number(
        pipe(
          subtract(positionAnalyse?.pnlTab?.lastResolvedTargetPrice),
          flip(divide)(defaultTargetPrice),
          multiply(100)
        )(defaultTargetPrice).toFixed(1)
      ),
    };
  });
};

// ========== Redux Selectors ==========

const positionAnalyseSelector = (state: IState) => state.positionAnalyse;

const positionAnalysePnlDataSelector = createSelector(
  [positionAnalyseSelector],
  (positionAnalyse: PositionAnalyticsState) => getPositionAnalysePnlData(positionAnalyse)
);

const positionAnalyseOrdersSelector = createSelector(
  [positionAnalyseSelector],
  positionAnalyse => positionAnalyse.orders
);

const positionAnalyseActiveOrdersSelector = createSelector(
  [positionAnalyseOrdersSelector, (_, activeAsset) => activeAsset],
  (orders, activeAsset) => getPositionAnalyseActiveOrders(orders, activeAsset)
);

const positionAnalyseTargetDatesSelector = createSelector(
  [
    (state, activeAsset) => positionAnalyseActiveOrdersSelector(state, activeAsset),
    productsSelector,
    positionAnalyseSelector,
  ],
  (ordersOfCurrentActiveAsset, products, positionAnalyse: PositionAnalyticsState) =>
    getPositionAnalyseTargetDates(ordersOfCurrentActiveAsset, products, positionAnalyse)
);

const positionAnalyseTargetDatesIndexMapSelector = createSelector(
  [positionAnalyseTargetDatesSelector],
  targetDates => getPositionAnalyseTargetDatesIndexMap(targetDates)
);

const positionAnalyseResultSpotPricesSelector = createSelector(
  [positionAnalyseSelector],
  (positionAnalyse: PositionAnalyticsState) =>
    getPositionAnalyseResultSpotPrices(positionAnalyse)
);

const positionAnalyseExpirySpotPricesSelector = createSelector(
  [positionAnalyseSelector],
  (positionAnalyse: PositionAnalyticsState) =>
    getPositionAnalyseExpirySpotPrices(positionAnalyse)
);

const positionAnalyseResultSpotPriceLabelsMaxSelector = createSelector(
  [positionAnalyseResultSpotPricesSelector],
  positionAnalyseResultSpotPrices =>
    getPositionAnalyseResultSpotPriceLabelsMax(positionAnalyseResultSpotPrices)
);

const positionAnalyseResultSpotPriceLabelsMinSelector = createSelector(
  [positionAnalyseResultSpotPricesSelector],
  positionAnalyseResultSpotPrices =>
    getPositionAnalyseResultSpotPriceLabelsMin(positionAnalyseResultSpotPrices)
);

const positionAnalyseExpiryPnlCoordinatesSelector = createSelector(
  [positionAnalyseExpirySpotPricesSelector, positionAnalyseSelector],
  (expirySpotPrices, positionAnalyse) =>
    getPositionAnalyseExpiryPnlCoordinates(expirySpotPrices, positionAnalyse)
);

const positionAnalyseTargetDatePnlCoordinatesSelector = createSelector(
  [
    positionAnalyseExpirySpotPricesSelector,
    positionAnalyseSelector,
    (_, defaultTargetPrice) => defaultTargetPrice,
  ],
  (expirySpotPrices, positionAnalyse, defaultTargetPrice) =>
    getPositionAnalyseTargetDatePnlCoordinates(
      expirySpotPrices,
      positionAnalyse,
      defaultTargetPrice
    )
);

const positionAnalyzeSelectedContractsSelector = (state: IState) =>
  state.positionAnalyse.pnlTab.selectedRows;

const initiallySelectedPositionIdsSelector = (state: IState) =>
  state.positionAnalyse.initiallySelectedPositionIds;

const selectedAssetsForAnalyticsSelector = createSelector(
  [initiallySelectedPositionIdsSelector, productsSelector, allOpenPositionsSelector],
  (initiallySelectedPositionIds, products, liveOpenPositions) => {
    const assetsFromInitialSelection = new Set(
      initiallySelectedPositionIds
        .map(id => products[id]?.underlying_asset?.symbol)
        .filter(Boolean)
    );
    return Array.from(assetsFromInitialSelection).filter(asset =>
      liveOpenPositions.some(p => p.product.underlying_asset.symbol === asset)
    );
  }
);

const positionAnalyzeExpiryMaxProfitSelector = createSelector(
  [positionAnalyseSelector],
  positionAnalyze => {
    const pnls = positionAnalyze?.positionAnalytics.data?.expiry_pnls ?? [];
    if (pnls.length === 0) return 0;
    const maxPnl = pipe(
      map((expiryPnl: PNLData) => parseFloat(expiryPnl.pnl)),
      // Filter out Infinity values for axis calculation
      filter((pnl: number) => Number.isFinite(pnl)),
      reduce<number, number>(max, 0)
    )(pnls);
    return maxPnl;
  }
);

const positionAnalyzeExpiryMaxLossSelector = createSelector(
  [positionAnalyseSelector],
  positionAnalyze => {
    const pnls = positionAnalyze?.positionAnalytics.data?.expiry_pnls ?? [];
    if (pnls.length === 0) return 0;
    const minPnl = pipe(
      map((expiryPnl: PNLData) => parseFloat(expiryPnl.pnl)),
      // Filter out -Infinity values for axis calculation
      filter((pnl: number) => Number.isFinite(pnl)),
      reduce<number, number>((a, b) => Math.min(a, b), 0)
    )(pnls);
    return Math.abs(Math.min(0, minPnl));
  }
);

const positionAnalyzeResultMaxProfitSelector = createSelector(
  [positionAnalyseSelector],
  positionAnalyze => {
    const pnls = positionAnalyze?.positionAnalytics.data?.result_pnls ?? [];
    if (pnls.length === 0) return 0;
    const maxPnl = pipe(
      map((expiryPnl: PNLData) => parseFloat(expiryPnl.pnl)),
      // Filter out Infinity values for axis calculation
      filter((pnl: number) => Number.isFinite(pnl)),
      reduce<number, number>(max, 0)
    )(pnls);
    return maxPnl;
  }
);

const positionAnalyzeResultMaxLossSelector = createSelector(
  [positionAnalyseSelector],
  positionAnalyze => {
    const pnls = positionAnalyze?.positionAnalytics.data?.result_pnls ?? [];
    if (pnls.length === 0) return 0;
    const minPnl = pipe(
      map((expiryPnl: PNLData) => parseFloat(expiryPnl.pnl)),
      // Filter out -Infinity values for axis calculation
      filter((pnl: number) => Number.isFinite(pnl)),
      reduce<number, number>((a, b) => Math.min(a, b), 0)
    )(pnls);
    return Math.abs(Math.min(0, minPnl));
  }
);

// Note: The following selectors require Zustand data and should be used with useMemo in components:
// - getPositionAnalyseProductTickerPairList -> use with allOptionsTicker
// - getPositionAnalyseOpenInterestCategories -> use with productTickerPairList
// - getPositionAnalyseGroupOiByStrikePricesCoordinates -> use with productTickerPairList and oiMap
// - getPositionAnalyseOpenInterestMax -> use with productTickerPairList and oiMap
// - getPositionAnalyseGreeksAndPnLData -> use with Zustand maps

// ========== Exports ==========

export {
  // Pure Functions (Zustand-compatible)
  getPositionAnalysePnlData,
  getPositionAnalyseActiveOrders,
  getPositionAnalyseTargetDates,
  getPositionAnalyseTargetDatesIndexMap,
  getPositionAnalyseGreeksAndPnLData,
  getPositionAnalyseResultSpotPrices,
  getPositionAnalyseExpirySpotPrices,
  getPositionAnalyseProductTickerPairList,
  getPositionAnalyseResultSpotPriceLabelsMax,
  getPositionAnalyseResultSpotPriceLabelsMin,
  getPositionAnalyseOpenInterestCategories,
  getPositionAnalyseGroupOiByStrikePricesCoordinates,
  getPositionAnalyseOpenInterestMax,
  getPositionAnalyseExpiryPnlCoordinates,
  getPositionAnalyseTargetDatePnlCoordinates,
  // Redux Selectors
  positionAnalyseSelector,
  positionAnalysePnlDataSelector,
  positionAnalyseOrdersSelector,
  positionAnalyseActiveOrdersSelector,
  positionAnalyseTargetDatesSelector,
  positionAnalyseTargetDatesIndexMapSelector,
  positionAnalyseResultSpotPricesSelector,
  positionAnalyseExpirySpotPricesSelector,
  positionAnalyseResultSpotPriceLabelsMaxSelector,
  positionAnalyseResultSpotPriceLabelsMinSelector,
  positionAnalyseExpiryPnlCoordinatesSelector,
  positionAnalyseTargetDatePnlCoordinatesSelector,
  positionAnalyzeSelectedContractsSelector,
  initiallySelectedPositionIdsSelector,
  selectedAssetsForAnalyticsSelector,
  positionAnalyzeExpiryMaxProfitSelector,
  positionAnalyzeExpiryMaxLossSelector,
  positionAnalyzeResultMaxProfitSelector,
  positionAnalyzeResultMaxLossSelector,
};
